import {
  Users,
  CalendarCheck,
  FileText,
  Bell,
  UserPlus,
  Play,
  CheckCircle2,
  MapPin,
  BarChart3,
  Shield,
  ChevronRight,
  ChevronDown,
  CalendarDays,
} from "lucide-react";
import { useContext, useEffect, useMemo, useRef, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { useNavigate, useSearchParams } from "react-router-dom";
import { AuthContext } from "../../Moduels/Context/AuthContext";
import { LEAVE_APPROVAL_NAV_STATE } from "../../Moduels/leave/leaveQueueEvents";
import { buildActionItems, countActionItems, useAttendanceApprovalCount } from "../../services/actionItems";
import EmployeeLifecycleCard from "../dashboard/EmployeeLifecycleCard";
import { getQuickActionsForRole } from "../../config/dashboardQuickActions";
import { filterQuickActionsByPageAccess } from "../../services/pageAccessService";
import { isPayrollDashboardRole, normalizeRole, R } from "../../rbac/access";
import {
  dashboardOverviewQueryKey,
  getDashboardLocations,
  getDashboardOverview,
  readDashboardCache,
} from "../../services/dashboardApi";
import { orgScope } from "../../services/orgScope";
import { formatInrCompact } from "../../utils/formatCurrency";
import { normalizeApiError } from "../../utils/normalizeApiError";
import { useSetupProgress } from "../../Moduels/Context/SetupProgressContext";
import SetupProgressCard from "../setup/SetupProgressCard";
import OrgProfileBanner from "../setup/OrgProfileBanner";
import FuelBillReviewCard, { usePendingFuelReview } from "../dashboard/FuelBillReviewCard";
import "../../styles/dashboard-home.css";

const OPS_ACTIONS = [
  { id: "add-employee", label: "Add Employee", Icon: UserPlus, action: { type: "navigate", path: "/employees/add" } },
  { id: "run-payroll", label: "Run Payroll", Icon: Play, action: { type: "navigate", path: "/payroll-process?tab=periods" } },
  { id: "approvals", label: "Approvals", Icon: CheckCircle2, action: { type: "navigate", path: "/approvals" } },
  { id: "payslips", label: "Generate Payslips", Icon: FileText, action: { type: "navigate", path: "/payslip?tab=bulk" } },
];

function greetingWord(date = new Date()) {
  const hour = date.getHours();
  if (hour < 12) return "Good Morning";
  if (hour < 17) return "Good Afternoon";
  return "Good Evening";
}

function firstName(user) {
  if (!user) return "there";
  const nested = user.user?.full_name || user.user?.username;
  if (nested) return nested.split(" ")[0];
  const employee = user.employee?.first_name || user.employee?.name?.split(" ")[0];
  if (employee) return employee;
  return user.full_name?.split(" ")?.[0] || user.first_name || user.username || "there";
}

function formatLongDate(date = new Date()) {
  return date.toLocaleDateString("en-IN", {
    weekday: "long",
    day: "numeric",
    month: "short",
    year: "numeric",
  });
}

function formatActivityWhen(iso) {
  if (!iso) return "—";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "—";
  return d.toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" });
}

function statusClass(status) {
  const key = String(status || "").toLowerCase();
  if (key === "on track") return "on-track";
  if (key === "due soon") return "due-soon";
  if (key === "due") return "due";
  if (key === "overdue") return "overdue";
  return "na";
}

function Skel({ className = "md" }) {
  return <span className={`dash-skel ${className}`} aria-hidden />;
}

function SectionError({ message, onRetry }) {
  return (
    <div className="dash-section-error" role="alert">
      <span>{message}</span>
      <button type="button" className="dash-link" onClick={onRetry}>Retry</button>
    </div>
  );
}

function LocationMenu({ locations, value, label, onChange, disabled }) {
  const [open, setOpen] = useState(false);
  const rootRef = useRef(null);
  const options = [{ id: "", name: "All Locations" }, ...locations];

  useEffect(() => {
    if (!open) return undefined;
    const onPointer = (event) => {
      if (!rootRef.current?.contains(event.target)) setOpen(false);
    };
    const onKey = (event) => {
      if (event.key === "Escape") setOpen(false);
    };
    document.addEventListener("mousedown", onPointer);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onPointer);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  return (
    <div className={`dash-loc${open ? " is-open" : ""}`} ref={rootRef}>
      <button
        type="button"
        id="dash-work-location"
        className="dash-loc-trigger"
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-label="Work location"
        disabled={disabled}
        onClick={() => setOpen((current) => !current)}
      >
        <MapPin size={15} aria-hidden />
        <span className="dash-loc-label">{label}</span>
        <ChevronDown size={14} aria-hidden className="dash-loc-chevron" />
      </button>
      {open ? (
        <ul className="dash-loc-menu" role="listbox" aria-label="Work location">
          {options.map((loc) => {
            const active = (loc.id || "") === value;
            return (
              <li key={loc.id || "all"} role="presentation">
                <button
                  type="button"
                  role="option"
                  aria-selected={active}
                  className={`dash-loc-option${active ? " is-active" : ""}`}
                  onClick={() => {
                    onChange(loc.id || "");
                    setOpen(false);
                  }}
                >
                  {loc.name}
                </button>
              </li>
            );
          })}
        </ul>
      ) : null}
    </div>
  );
}

function PayrollTrend({ series }) {
  const points = (series || []).filter((row) => row && !row.is_future);
  const values = points.map((row) => Math.max(0, (Number(row.gross_pay) || 0) - (Number(row.deductions) || 0)));
  if (!values.some((v) => v > 0)) {
    return <p className="dash-empty">No payroll trend yet.</p>;
  }
  const w = 520;
  const h = 148;
  const padL = 36;
  const padR = 8;
  const padT = 8;
  const padB = 22;
  const max = Math.max(...values, 1);
  const ticks = [1, 0.75, 0.5, 0.25, 0];
  const plotW = w - padL - padR;
  const plotH = h - padT - padB;
  const step = points.length > 1 ? plotW / (points.length - 1) : 0;
  const coords = values.map((v, i) => {
    const x = padL + i * step;
    const y = padT + (1 - v / max) * plotH;
    return [x, y];
  });
  const d = coords.map((p, i) => `${i === 0 ? "M" : "L"}${p[0].toFixed(1)},${p[1].toFixed(1)}`).join(" ");
  const labelEvery = points.length > 8 ? 2 : 1;

  return (
    <svg className="dash-trend" viewBox={`0 0 ${w} ${h}`} role="img" aria-label="Payroll net pay trend">
      {ticks.map((t) => {
        const y = padT + (1 - t) * plotH;
        return (
          <g key={t}>
            <line x1={padL} y1={y} x2={w - padR} y2={y} stroke="#e8eef5" strokeWidth="1" />
            <text x={padL - 6} y={y + 3} textAnchor="end" className="dash-axis">
              {t === 0 ? "0" : formatInrCompact(max * t).replace("₹", "")}
            </text>
          </g>
        );
      })}
      <path d={d} fill="none" stroke="#1a437d" strokeWidth="2.25" strokeLinejoin="round" strokeLinecap="round" />
      {coords.map((p, i) => (
        <g key={points[i].month}>
          <circle cx={p[0]} cy={p[1]} r="3.5" fill="#1a437d">
            <title>{`${points[i].month}: ${formatInrCompact(values[i])}`}</title>
          </circle>
          {i % labelEvery === 0 ? (
            <text x={p[0]} y={h - 4} textAnchor="middle" className="dash-axis">{points[i].month}</text>
          ) : null}
        </g>
      ))}
    </svg>
  );
}

function AttendanceDonut({ present, total }) {
  const pct = total ? Math.round((present / total) * 100) : 0;
  const r = 46;
  const c = 2 * Math.PI * r;
  const dash = (pct / 100) * c;
  return (
    <div className="dash-donut" aria-hidden>
      <svg viewBox="0 0 128 128">
        <circle cx="64" cy="64" r={r} fill="none" stroke="#e8eef5" strokeWidth="14" />
        <circle
          cx="64"
          cy="64"
          r={r}
          fill="none"
          stroke="#1a437d"
          strokeWidth="14"
          strokeLinecap="round"
          strokeDasharray={`${dash} ${c - dash}`}
          transform="rotate(-90 64 64)"
        />
      </svg>
      <div className="dash-donut-label">
        <strong>{pct}%</strong>
        <span>Present</span>
      </div>
    </div>
  );
}

function buildWorkflow(payroll, attendance) {
  const status = String(payroll?.status || "").toLowerCase();
  const execution = String(payroll?.execution_status || "").toLowerCase();
  const lifecycle = String(payroll?.lifecycle_status || "").toLowerCase();
  const failed = execution === "failed";
  const calculated = ["processed", "locked", "approved"].includes(status)
    || ["completed", "processed", "success", "done"].includes(execution);
  const reviewed = ["verified", "approved", "locked"].includes(lifecycle) || calculated;
  const payslips = calculated;
  const bank = lifecycle === "locked" || ["disbursed", "transferred", "paid"].includes(execution);
  const attendanceDone = Boolean(payroll?.attendance_locked)
    || calculated
    || (attendance?.ok && attendance.total > 0 && attendance.unmarked === 0 && attendance.marked > 0);

  const flags = [
    { id: "attendance", label: "Attendance", done: attendanceDone, blocked: false, doneLabel: "Completed" },
    { id: "calculation", label: "Calculation", done: calculated, blocked: failed, doneLabel: "Completed" },
    { id: "review", label: "Review", done: reviewed && calculated, blocked: false, doneLabel: "Completed" },
    { id: "payslips", label: "Payslips", done: payslips, blocked: false, doneLabel: "Generated" },
    { id: "bank", label: "Bank Transfer", done: bank, blocked: false, doneLabel: "Completed" },
    { id: "completed", label: "Completed", done: bank && calculated, blocked: false, doneLabel: "Completed" },
  ];

  let seenCurrent = false;
  return flags.map((step) => {
    if (step.blocked) return { ...step, state: "blocked", detail: "Blocked" };
    if (step.done) return { ...step, state: "done", detail: step.doneLabel };
    if (!seenCurrent) {
      seenCurrent = true;
      return { ...step, state: "current", detail: "Pending" };
    }
    return { ...step, state: "upcoming", detail: "" };
  });
}

export default function DashboardHome() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { user, role, pageAccess, loading: authLoading } = useContext(AuthContext);
  const setup = useSetupProgress();
  const locationId = searchParams.get("location") || "";
  const normalizedRole = normalizeRole(role);
  const canLoadMetrics = isPayrollDashboardRole(normalizedRole);
  const canRunPayroll = !authLoading && (normalizedRole === R.ADMIN || normalizedRole === R.HR);
  const isManager = normalizedRole === R.MANAGER;
  const setupIncomplete = canRunPayroll && setup?.progress && !setup.progress.loading && (setup.progress.percent ?? 0) < 100;

  const locationsQuery = useQuery({
    queryKey: ["dashboard-locations", orgScope(), role],
    queryFn: getDashboardLocations,
    enabled: canLoadMetrics && Boolean(role),
    staleTime: 5 * 60_000,
  });

  const fuelReviewQuery = usePendingFuelReview({ enabled: canRunPayroll });

  const overviewQuery = useQuery({
    queryKey: dashboardOverviewQueryKey(role, locationId || "all"),
    queryFn: () => getDashboardOverview(locationId || undefined),
    enabled: canLoadMetrics && Boolean(role),
    staleTime: 30_000,
    placeholderData: locationId ? undefined : readDashboardCache,
    retry: 1,
  });

  const locations = locationsQuery.data || [];

  useEffect(() => {
    if (!locationId || locationsQuery.isLoading || !locationsQuery.data) return;
    const known = locationsQuery.data.some((loc) => loc.id === locationId);
    if (!known) {
      const next = new URLSearchParams(searchParams);
      next.delete("location");
      setSearchParams(next, { replace: true });
    }
  }, [locationId, locationsQuery.data, locationsQuery.isLoading, searchParams, setSearchParams]);

  useEffect(() => {
    if (!locationId || !overviewQuery.isError) return;
    if (overviewQuery.error?.response?.status === 404) {
      const next = new URLSearchParams(searchParams);
      next.delete("location");
      setSearchParams(next, { replace: true });
    }
  }, [locationId, overviewQuery.isError, overviewQuery.error, searchParams, setSearchParams]);

  const setLocation = (id) => {
    const next = new URLSearchParams(searchParams);
    if (!id) next.delete("location");
    else next.set("location", id);
    setSearchParams(next, { replace: true });
  };

  const overview = overviewQuery.data;
  const dataMatches = Boolean(overview?.scope) && (overview.scope.location_id || "") === locationId;
  const showSkeleton = canLoadMetrics && !overviewQuery.isError && !dataMatches;
  // Only a specific location with no workforce hides the cards. A new organisation
  // (All Locations, zero employees) still gets the full layout with zero/empty states.
  const emptyLocation = Boolean(locationId) && overview?.scope?.has_workforce === false;
  const hasWorkforce = overviewQuery.isError
    ? Boolean(dataMatches && !emptyLocation)
    : !dataMatches || !emptyLocation;
  const selectedName = locationId
    ? (locations.find((loc) => loc.id === locationId)?.name || overview?.scope?.location_name || "Location")
    : "All Locations";

  const quickActions = useMemo(() => {
    const source = canLoadMetrics ? OPS_ACTIONS : getQuickActionsForRole(role);
    return filterQuickActionsByPageAccess(source, pageAccess);
  }, [canLoadMetrics, role, pageAccess]);

  const employees = overview?.employees;
  const payroll = overview?.payroll;
  const attendance = overview?.attendance_today;
  const leave = overview?.leave_summary;
  const activity = overview?.recent_activity;
  const modules = overview?.compliance_modules || [];
  const noEmployees = dataMatches && !locationId && (employees?.total ?? 0) === 0;
  const hasPayroll = Boolean(payroll?.has_payroll_data);
  const noPayrollYet = !hasPayroll && !payroll?.latest_run_id && !payroll?.metrics_run_id;
  const readiness = overview?.payroll_readiness;
  const attendancePct = attendance?.total
    ? Math.round((attendance.present / attendance.total) * 100)
    : null;

  // Every pending item across modules (employees, attendance, leave, payroll, compliance).
  const attendanceApprovals = useAttendanceApprovalCount(canLoadMetrics);
  const fuelPending = fuelReviewQuery.data?.enabled ? (fuelReviewQuery.data.total || 0) : 0;
  const allActions = buildActionItems(overview, navigate, {
    attendanceApprovals,
    fuelPending,
    onFuelReview: () => document.getElementById("dash-fuel-review")?.scrollIntoView({ behavior: "smooth", block: "start" }),
  });
  const pendingActions = countActionItems(allActions);
  // The card shows the top 3; "View all" opens the full list on the Approvals page.
  const actions = allActions.slice(0, 3);

  const workflow = buildWorkflow(payroll, attendance);

  const netChange = (() => {
    const trends = (overview?.monthly_trends || []).filter((row) => !row.is_future);
    return trends.length ? trends[trends.length - 1]?.net_change ?? 0 : 0;
  })();

  const pageError = overviewQuery.isError && !overview;

  return (
    <div className={`dash-home ${overviewQuery.isFetching && dataMatches ? "dash-busy" : ""}`}>
      <header className="dash-header">
        <div className="dash-greeting">
          <h1>{greetingWord()}, {firstName(user)}!</h1>
          <p>Here&apos;s what&apos;s happening in your organization today.</p>
        </div>
        <div className="dash-header-side">
          {canLoadMetrics ? (
            <div className="dash-loc-slot">
              <LocationMenu
                locations={locations}
                value={locationId}
                label={locationsQuery.isLoading && !locations.length ? "Loading…" : selectedName}
                onChange={setLocation}
                disabled={locationsQuery.isLoading && !locations.length}
              />
              {locationsQuery.isError ? (
                <SectionError message="Unable to load work locations." onRetry={() => locationsQuery.refetch()} />
              ) : null}
            </div>
          ) : null}
          <div className="dash-date">{formatLongDate()}</div>
        </div>
      </header>

      {setupIncomplete ? <SetupProgressCard showRunPayroll /> : null}
      <OrgProfileBanner compact />

      {pageError ? (
        <section className="dash-card" role="alert">
          <SectionError
            message={normalizeApiError(overviewQuery.error) || "Unable to load dashboard data."}
            onRetry={() => overviewQuery.refetch()}
          />
        </section>
      ) : null}

      {canLoadMetrics && dataMatches && emptyLocation ? (
        <section className="dash-card">
          <div className="dash-card-head">
            <h2>No data available for this location.</h2>
          </div>
          <p className="dash-empty">Choose another work location or return to All Locations.</p>
        </section>
      ) : null}

      {canLoadMetrics && hasWorkforce ? (
        <section className="dash-kpis" aria-label="Today's summary">
          <Kpi
            icon={<Users size={16} />}
            label="Total Employees"
            value={showSkeleton ? null : employees?.total ?? 0}
            sub={showSkeleton ? null : (netChange > 0 ? `+${netChange} this month` : "Active employees")}
          />
          <Kpi
            icon={<CalendarCheck size={16} />}
            label="Attendance Today"
            value={showSkeleton ? null : noEmployees ? "0 / 0" : (attendance?.ok && attendance?.available ? `${attendance.present} / ${attendance.total}` : "—")}
            sub={showSkeleton ? null : noEmployees ? "No employees yet" : (attendance?.ok === false ? "Unable to load" : attendancePct == null ? "No attendance data" : `${attendancePct}% Present`)}
          />
          <Kpi
            icon={<FileText size={16} />}
            label={hasPayroll && payroll?.period_label ? `Payroll – ${payroll.period_label}` : "Payroll"}
            value={showSkeleton ? null : (hasPayroll ? formatInrCompact(payroll?.net_pay) : "₹0")}
            sub={showSkeleton ? null : (hasPayroll
              ? `${payroll?.processed_count ?? employees?.total ?? 0} Employees`
              : (employees?.total ?? 0) === 0 ? "0 Employees" : "No payroll processed yet")}
            subTone={payroll?.has_payroll_data ? "ok" : undefined}
          />
          <Kpi
            icon={<Bell size={16} />}
            label="Pending Actions"
            value={showSkeleton ? null : pendingActions}
            valueTone={!showSkeleton && pendingActions > 0 ? "alert" : undefined}
            sub={showSkeleton ? null : (pendingActions > 0 ? "Needs your attention" : "Up to date")}
            subTone={!showSkeleton && pendingActions > 0 ? "alert" : undefined}
            onClick={() => document.getElementById("dash-action-required")?.scrollIntoView({ behavior: "smooth", block: "start" })}
          />
        </section>
      ) : null}

      {canLoadMetrics && hasWorkforce ? (
        <section className="dash-card" id="dash-action-required" aria-labelledby="dash-action-title">
          <div className="dash-card-head">
            <div>
              <h2 id="dash-action-title"><span className="dash-head-icon warn" aria-hidden><Bell size={15} /></span>Action Required</h2>
              <p className="dash-card-sub">Items that need your attention</p>
            </div>
            <button type="button" className="dash-link" onClick={() => navigate("/approvals")}>View all <ChevronRight size={14} /></button>
          </div>
          {showSkeleton ? (
            <div>
              <Skel className="lg" />
              <div style={{ height: 8 }} />
              <Skel />
            </div>
          ) : actions.length === 0 ? (
            <p className="dash-empty">{noEmployees ? "No actions required yet." : "No pending actions. Everything is up to date."}</p>
          ) : (
            actions.map((item) => (
              <div className="dash-action" key={item.key}>
                <i className={`dash-dot ${item.tone}`} aria-hidden />
                <div>
                  <span className="dash-action-group">{item.group}</span>
                  <strong>{item.title}</strong>
                  <span>{item.detail}</span>
                </div>
                <button type="button" className={`dash-inline-link ${item.tone}`} onClick={item.onClick}>
                  {item.label} <ChevronRight size={14} />
                </button>
              </div>
            ))
          )}
        </section>
      ) : null}

      {canRunPayroll ? <FuelBillReviewCard query={fuelReviewQuery} /> : null}

      {isManager ? <EmployeeLifecycleCard /> : null}
      {!canLoadMetrics ? <QuickActions actions={quickActions} onNavigate={navigate} /> : null}

      {canLoadMetrics && hasWorkforce ? (
        <div className="dash-split">
          <section className="dash-card" aria-labelledby="dash-pay-title">
            <div className="dash-card-head">
              <h2 id="dash-pay-title"><span className="dash-head-icon" aria-hidden><BarChart3 size={15} /></span>Payroll Overview</h2>
            </div>
            {showSkeleton ? <Skel className="lg" /> : (
              <>
                <div className="dash-pay-figs">
                  <div>
                    <span>Gross Pay</span>
                    <strong>{formatInrCompact(hasPayroll ? payroll?.gross_pay : 0)}</strong>
                  </div>
                  <div>
                    <span>Deductions</span>
                    <strong>{formatInrCompact(hasPayroll ? payroll?.deductions : 0)}</strong>
                  </div>
                  <div className="is-net">
                    <span>Net Pay</span>
                    <strong>{formatInrCompact(hasPayroll ? payroll?.net_pay : 0)}</strong>
                  </div>
                </div>
                {payroll?.has_payroll_data ? (
                  <PayrollTrend series={overview?.monthly_trends} />
                ) : (
                  <p className="dash-empty">{locationId ? `No payroll data for ${selectedName}.` : "No payroll processed yet."}</p>
                )}
                <button type="button" className="dash-link dash-card-foot" onClick={() => navigate("/reports/payroll")}>
                  View Payroll Analytics <ChevronRight size={14} />
                </button>
              </>
            )}
          </section>

          <section className="dash-card" aria-labelledby="dash-att-title">
            <div className="dash-card-head">
              <h2 id="dash-att-title"><span className="dash-head-icon" aria-hidden><CalendarDays size={15} /></span>Attendance Today</h2>
            </div>
            {showSkeleton ? <Skel className="lg" /> : attendance?.ok === false ? (
              <SectionError message="Unable to load attendance data." onRetry={() => overviewQuery.refetch()} />
            ) : !attendance?.available && !noEmployees ? (
              <p className="dash-empty">No data available for this location.</p>
            ) : (
              <div className="dash-att-layout">
                <AttendanceDonut present={attendance?.present ?? 0} total={attendance?.total ?? 0} />
                <div className="dash-att-legend">
                  {[
                    ["present", "Present", attendance?.present ?? 0],
                    ["leave", "Leave", attendance?.leave ?? 0],
                    ["absent", "Absent", attendance?.absent ?? 0],
                    ["half", "Half Day", attendance?.half_day ?? 0],
                    ["unmarked", "Unmarked", attendance?.unmarked ?? 0],
                  ].map(([key, label, count]) => (
                    <div className="dash-att-row" key={key}>
                      <span className="dash-att-name"><i className={`dash-dot ${key}`} aria-hidden />{label}</span>
                      <b>{count}</b>
                    </div>
                  ))}
                </div>
              </div>
            )}
            <button type="button" className="dash-link dash-card-foot" onClick={() => navigate("/attendance/dashboard")}>
              View Attendance <ChevronRight size={14} />
            </button>
          </section>
        </div>
      ) : null}

      {canLoadMetrics && hasWorkforce ? (
        <section className="dash-card" aria-label="Payroll status">
          <div className="dash-card-head">
            <h2><span className="dash-head-icon" aria-hidden><FileText size={15} /></span>Payroll Status{!noPayrollYet && payroll?.period_label ? ` – ${payroll.period_label}` : ""}</h2>
          </div>
          {showSkeleton ? <Skel className="lg" /> : noPayrollYet ? (
            <>
              <p className="dash-empty">
                No payroll processed yet.
                <br />
                {readiness && !readiness.complete
                  ? "Start by completing your payroll setup."
                  : "Run your first payroll when you are ready."}
              </p>
              {canRunPayroll ? (
                <div className="dash-flow-action">
                  <button
                    type="button"
                    className="dash-continue"
                    onClick={() => navigate(
                      readiness && !readiness.complete && readiness.current_step_route
                        ? readiness.current_step_route
                        : "/payroll-process?tab=periods"
                    )}
                  >
                    {readiness && !readiness.complete ? "Continue Payroll Setup" : "Run Payroll"} <ChevronRight size={15} />
                  </button>
                </div>
              ) : null}
            </>
          ) : (
            <>
              <div className="dash-flow" role="list">
                {workflow.map((step, index) => (
                  <div className={`dash-step ${step.state}`} key={step.id} role="listitem">
                    <div className="dash-step-body">
                      <span className="dash-step-mark" aria-hidden>{index + 1}</span>
                      <span className="dash-step-label">{step.label}</span>
                      <span className="dash-step-detail">{step.detail}</span>
                    </div>
                    {index < workflow.length - 1 ? <span className="dash-step-line" aria-hidden /> : null}
                  </div>
                ))}
              </div>
              {workflow.some((step) => step.state !== "done") ? (
                <div className="dash-flow-action">
                  <button type="button" className="dash-continue" onClick={() => navigate("/payroll-process?tab=process")}>
                    Continue Payroll <ChevronRight size={15} />
                  </button>
                </div>
              ) : null}
            </>
          )}
        </section>
      ) : null}

      {canLoadMetrics && hasWorkforce ? (
        <div className="dash-pair">
          <section className="dash-card" aria-labelledby="dash-leave-title">
            <div className="dash-card-head">
              <h2 id="dash-leave-title"><span className="dash-head-icon" aria-hidden><CalendarDays size={15} /></span>Leave Summary</h2>
            </div>
            {showSkeleton ? <Skel className="lg" /> : leave?.ok === false ? (
              <SectionError message="Unable to load leave data." onRetry={() => overviewQuery.refetch()} />
            ) : (
              <div className="dash-leave-grid">
                <button type="button" onClick={() => navigate("/leave?tab=approval", { state: LEAVE_APPROVAL_NAV_STATE })}>
                  <strong>{leave?.pending ?? 0}</strong>
                  <span>Pending Requests</span>
                </button>
                <button type="button" onClick={() => navigate("/leave")}>
                  <strong>{leave?.on_leave_today ?? 0}</strong>
                  <span>On Leave Today</span>
                </button>
                <button type="button" onClick={() => navigate("/leave")}>
                  <strong>{leave?.upcoming ?? 0}</strong>
                  <span>Upcoming Leave</span>
                </button>
              </div>
            )}
            <button type="button" className="dash-link dash-card-foot" onClick={() => navigate("/leave")}>
              View Leave <ChevronRight size={14} />
            </button>
          </section>

          <section className="dash-card" aria-labelledby="dash-comp-title">
            <div className="dash-card-head">
              <h2 id="dash-comp-title"><span className="dash-head-icon" aria-hidden><Shield size={15} /></span>Compliance Status</h2>
            </div>
            {showSkeleton ? <Skel className="lg" /> : modules.map((item) => (
              <button
                key={item.key}
                type="button"
                className="dash-comp-row"
                onClick={() => item.route && navigate(item.route)}
              >
                <b>{item.label}</b>
                <span className={`dash-status ${statusClass(item.status)}`}>
                  <i aria-hidden />
                  {item.status}
                </span>
              </button>
            ))}
            <button type="button" className="dash-link dash-card-foot dash-card-foot-end" onClick={() => navigate("/statutory/pf")}>
              View Compliance <ChevronRight size={14} />
            </button>
          </section>
        </div>
      ) : null}

      {canLoadMetrics && hasWorkforce ? (
        <div className="dash-bottom">
        <section className="dash-card" aria-labelledby="dash-activity-title">
          <div className="dash-card-head">
            <h2 id="dash-activity-title"><span className="dash-head-icon" aria-hidden><BarChart3 size={15} /></span>Recent Activity</h2>
            <button
              type="button"
              className="dash-link"
              onClick={() => navigate(locationId ? `/dashboard/activity?location=${locationId}` : "/dashboard/activity")}
            >
              View all <ChevronRight size={14} />
            </button>
          </div>
          {showSkeleton ? <Skel className="lg" /> : activity?.ok === false ? (
            <SectionError message="Unable to load recent activity." onRetry={() => overviewQuery.refetch()} />
          ) : !(activity?.items || []).length ? (
            <p className="dash-empty">{noEmployees ? "No recent activity yet." : "No recent activity."}</p>
          ) : (
            activity.items.slice(0, 5).map((item, index) => (
              <div className="dash-activity" key={`${item.at}-${index}`}>
                <i className={`dash-dot ${["half", "present", "leave", "absent"][index % 4]}`} aria-hidden />
                <time dateTime={item.at}>{formatActivityWhen(item.at)}</time>
                <b>{item.subject ? `${item.activity} · ${item.subject}` : item.activity}</b>
                <span>{item.location}</span>
              </div>
            ))
          )}
        </section>
        <QuickActions actions={quickActions} onNavigate={navigate} />
        </div>
      ) : null}
    </div>
  );
}

function QuickActions({ actions, onNavigate }) {
  return (
    <section className="dash-card" aria-labelledby="dash-qa-title">
      <div className="dash-card-head">
        <h2 id="dash-qa-title"><span className="dash-head-icon" aria-hidden><Play size={15} /></span>Quick Actions</h2>
      </div>
      <div className="dash-qa">
        {actions.map((item) => {
          const Icon = item.Icon;
          return (
            <button
              key={item.id}
              type="button"
              onClick={() => item.action?.path && onNavigate(item.action.path)}
            >
              <Icon size={18} aria-hidden />
              {item.label}
            </button>
          );
        })}
      </div>
    </section>
  );
}

function Kpi({ icon, label, value, sub, onClick, valueTone, subTone }) {
  const Tag = onClick ? "button" : "article";
  return (
    <Tag className="dash-card dash-kpi" type={onClick ? "button" : undefined} onClick={onClick}>
      <div className="dash-kpi-top">
        <span className="dash-kpi-icon" aria-hidden>{icon}</span>
        <span className="dash-kpi-label">{label}</span>
      </div>
      <div className={`dash-kpi-value${valueTone ? ` is-${valueTone}` : ""}`}>{value == null ? <Skel className="lg" /> : value}</div>
      <div className={`dash-kpi-sub${subTone ? ` is-${subTone}` : ""}`}>{sub == null ? <Skel className="sm" /> : sub}</div>
    </Tag>
  );
}
